import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { CacheControlService } from './cache-control.service';
import { AppService } from '../../../app.service';
import { environment } from '../../../../environments/environment';

export interface CacheObjRow {
  object_id: number;
  object_name: string;
  redis_obj_key: string;
  input_filter: string;
  recordCount: number;
  is_cached: string;
  is_stale: string;
  update_date: string;
  switchVal: boolean;
}

@Component({
    selector: 'gtw-cache-control',
    templateUrl: './cache-control.component.html',
    styleUrls: ['./cache-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CacheControlComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  activeTab = 1;
  tabs = [
    { id: 1, name: 'Custom Cache', hover: false },
    { id: 2, name: 'System Cache', hover: false },
    { id: 3, name: 'Index Deletion', hover: false },
  ];

  // ── Tab 1: Custom Cache ──
  loading = false;
  hasEvictAccess = false;
  evictSelectedCacheValue = '';
  cacheDropdown: string[] = [];
  evictCacheLoading = false;
  cacheObjList: CacheObjRow[] = [];
  tableSearchText = '';

  pageSizeOptions = [10, 25, 50, 100];
  pageSize = 10;
  currentPage = 1;
  sortColumn = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // ── Tab 2: System Cache ──
  selectedCache = '';
  cacheDropdownObj: any = {};
  keyData: string[] = [];
  searchText = '';
  taskModel: string | null = null;
  showJSON = false;
  cacheKeyJsonView = '';
  showJsonLoader = false;
  showSelectMsg = true;

  // ── Tab 3: Index Deletion ──
  selectedType = '';
  indexDeletionDropdown = [
    { value: 'true', label: 'Delete All' },
    { value: 'false', label: 'Delete None' },
  ];
  deletionLoader = false;

  // ── Add Cache modal ──
  showAddCacheModal = false;
  addCacheLoading = false;
  addCacheMinimise = false;
  cacheFormSubmitted = false;
  maxFilterLength = 100;
  cacheForm: any = {
    object_key: '',
    object_name: '',
    proc_params_in: '',
    proc_params_out: '',
    proc_call: '',
    filter_params: '',
  };
  exampleOutList = [
    'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR',
  ];
  exampleInList = [
    'tax_year:String:null',
    'tax_year:String:null,combination_key:String:null',
    'tax_year:String:null,jcd_key:String:null,scenario:String:null',
    'tax_year:String:null,map_type:String:null',
  ];
  exampleProc = [
    'begin gtwusr01.pkg_globaltax_data_cache.get_code_combinations(?,?,?,?);end;',
    'begin gtwusr01.pkg_globaltax_data_cache.get_all_code_combinations(?,?,?,?,?);end;',
    'begin pkg_gt_validation_rules.get_group_obj_details_data(?,?,?,?,?,?); end;',
    'begin pkg_globaltax_data_cache.get_chart_acct_map(?,?,?,?,?);end;',
  ];
  filterConditions = [
    'tax_year:2016',
    'tax_year:2016,scenario:21,jcd_key:250',
    'tax_year:2016,scenario:21,jcd_key:250,source_sys:F',
    'tax_year:2016,scenario:21,jcd_key:250,source_sys:D',
  ];

  // ── Confirmation dialog ──
  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  // ── JSON Diff modal ──
  showDiffDialog = false;
  diffModalTitle = 'JSON Difference Modal';
  diffRedisCount = 0;
  diffDbCount = 0;
  diffRedisJsonView = '';
  diffDbJsonView = '';
  diffChangesView = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private cacheControlService: CacheControlService,
    private appService: AppService,
  ) {}

  // ────────────────── URL helpers (like system-settings) ──────────────────

  /**
   * Old AngularJS GENERAL_CONFIG.common_api_url = env.js commonApiUrl = '/batch'.
   * In environment.ts this is `batch_api_url`.
   */
  private get commonApiUrl(): string {
    return (environment as any).batch_api_url || environment.base_url;
  }

  private get baseUrl(): string {
    return environment.base_url;
  }

  // ────────────────── Client helpers (like system-settings) ──────────────────

  private get clientKey(): string {
    const user = this.appService.USER_SETTINGS?.user;
    return String(user?.client?.client_key ?? user?.client_key ?? '');
  }

  get clientName(): string {
    const user = this.appService.USER_SETTINGS?.user;
    return String(user?.client?.client_name || user?.client_name || '');
  }

  // ────────────────── Lifecycle ──────────────────

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInitialData(): void {
    this.loadCacheControlList();
    this.loadCacheKeys();
  }

  // ────────────────── Table search & pagination ──────────────────

  get filteredCacheObjList(): CacheObjRow[] {
    let list = this.cacheObjList;
    if (this.tableSearchText) {
      const s = this.tableSearchText.toLowerCase();
      list = list.filter(row =>
        (row.object_name || '').toLowerCase().includes(s)
        || (row.input_filter || '').toLowerCase().includes(s)
        || (row.redis_obj_key || '').toLowerCase().includes(s)
        || String(row.recordCount || '').includes(s)
        || (row.is_cached || '').toLowerCase().includes(s)
        || (row.update_date || '').toLowerCase().includes(s)
      );
    }
    if (this.sortColumn && this.sortDirection) {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      const col = this.sortColumn as keyof CacheObjRow;
      const numericCols: string[] = ['recordCount'];
      const dateCols: string[] = ['update_date'];
      list = [...list].sort((a, b) => {
        const va = a[col] ?? '';
        const vb = b[col] ?? '';
        if (numericCols.includes(col)) {
          return (Number(va) - Number(vb)) * dir;
        }
        if (dateCols.includes(col)) {
          return (new Date(String(va)).getTime() - new Date(String(vb)).getTime()) * dir;
        }
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    return list;
  }

  get paginatedCacheObjList(): CacheObjRow[] {
    const filtered = this.filteredCacheObjList;
    const start = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredCacheObjList.length / this.pageSize));
  }

  get paginationInfoText(): string {
    const filtered = this.filteredCacheObjList;
    if (filtered.length === 0) return '0 items';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, filtered.length);
    return `${start}-${end} of ${filtered.length} items`;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.cdr.detectChanges();
    }
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : this.sortDirection === 'desc' ? '' : 'asc';
      if (!this.sortDirection) this.sortColumn = '';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  // ────────────────── Filter chars ──────────────────

  get remainingFilterChars(): number {
    return this.maxFilterLength - String(this.cacheForm?.filter_params || '').length;
  }

  get isFilterLimitReached(): boolean {
    return this.remainingFilterChars <= 0;
  }

  // ────────────────── Data loading ──────────────────

  private loadCacheControlList(): void {
    if (!this.clientKey) return;
    this.loading = true;
    this.cdr.detectChanges();

    this.cacheControlService
      .fetchCacheObjControlDetails(this.commonApiUrl, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          const payload = resp?.data || resp;
          if (String(payload?.callSuccess) === '1') {
            const list = payload?.cacheObjControlDetails || [];
            this.cacheObjList = list;
            this.hasEvictAccess = !!payload?.hasEvictAccess;
            this.currentPage = 1;
          } else {
            this.gtwSnackbarService.openSnackBar(payload?.errorMessage || 'Unable to retrieve cache details.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.loading = false;
          console.error('[CacheControl] fetchCacheObjControlDetails error:', err);
          this.gtwSnackbarService.openSnackBar('Error loading cache details.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadCacheKeys(): void {
    this.cacheControlService
      .getAllCacheKeysAndCount(this.commonApiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.cacheDropdownObj = resp?.CacheNameAllKeyName || {};
          this.cacheDropdown = Object.keys(this.cacheDropdownObj);
          this.cdr.detectChanges();
        },
        error: () => {
          this.cdr.detectChanges();
        },
      });
  }

  // ────────────────── Confirmation dialog ──────────────────

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  confirmRefreshAll(): void {
    this.confirmAction(
      'Refresh All',
      'Are you sure you want to refresh all cache objects?',
      () => this.refreshAllCacheObj('Y'),
    );
  }

  confirmEvictAll(): void {
    this.confirmAction(
      'Evict All Cache',
      'Are you sure you want to evict all cache objects?',
      () => this.evictAllCache(),
    );
  }

  confirmEvictSelected(): void {
    if (!this.evictSelectedCacheValue) return;
    this.confirmAction(
      'Evict Selected Cache',
      'Are you sure you want to evict selected cache objects?',
      () => this.evictSelectedCache(this.evictSelectedCacheValue),
    );
  }

  confirmDeletion(): void {
    if (!this.selectedType) return;
    const label = this.indexDeletionDropdown.find(d => d.value === this.selectedType)?.label || this.selectedType;
    this.confirmAction(
      'Index Deletion',
      `Are you sure you want to proceed with "${label}"?`,
      () => this.performDeletion(),
    );
  }

  // ────────────────── Tab 1: Custom Cache actions ──────────────────

  addCache(): void {
    this.cacheFormSubmitted = false;
    this.addCacheLoading = false;
    this.addCacheMinimise = false;
    this.cacheForm = {
      object_key: '',
      object_name: '',
      proc_params_in: '',
      proc_params_out: '',
      proc_call: '',
      filter_params: '',
    };
    this.showAddCacheModal = true;
    this.cdr.detectChanges();
  }

  closeAddCacheModal(): void {
    this.showAddCacheModal = false;
    this.cacheFormSubmitted = false;
    this.cdr.detectChanges();
  }

  saveCacheDefinition(): void {
    this.cacheFormSubmitted = true;
    const requiredMissing =
      !this.cacheForm.object_key
      || !this.cacheForm.object_name
      || !this.cacheForm.proc_params_in
      || !this.cacheForm.proc_params_out
      || !this.cacheForm.proc_call
      || !this.cacheForm.filter_params;
    if (requiredMissing) {
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }
    if (this.remainingFilterChars < 0) {
      this.gtwSnackbarService.openSnackBar('Filter condition max length reached', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    this.addCacheLoading = true;
    const payload = { ...this.cacheForm, client_key: this.clientKey };
    this.cacheControlService.saveCache(this.commonApiUrl, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.addCacheLoading = false;
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Cache definition saved successfully.', 'gtw-snackbar--green');
            this.showAddCacheModal = false;
            this.loadCacheControlList();
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Cache save failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.addCacheLoading = false;
          this.gtwSnackbarService.openSnackBar('Cache save failed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  refreshAllCacheObj(confirm: string): void {
    this.loading = true;
    this.cdr.detectChanges();
    this.cacheControlService
      .refreshAllCachedObject(this.commonApiUrl, confirm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Refresh operation completed successfully.', 'gtw-snackbar--green');
            this.loadCacheControlList();
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Refresh all failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Refresh all failed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  evictAllCache(): void {
    this.evictCacheLoading = true;
    this.cdr.detectChanges();
    this.cacheControlService.evictAllCache(this.commonApiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.evictCacheLoading = false;
          if (String(resp?.callSuccess) === '1' || resp?.callSuccess === 1) {
            this.gtwSnackbarService.openSnackBar('All cache evicted', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Evict all failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.evictCacheLoading = false;
          this.gtwSnackbarService.openSnackBar('Evict all failed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  evictSelectedCache(cache: string): void {
    if (!cache) return;
    this.evictCacheLoading = true;
    this.cdr.detectChanges();
    this.cacheControlService.evictCacheObj(this.commonApiUrl, cache)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.evictCacheLoading = false;
          if (String(resp?.callSuccess) === '1' || resp?.callSuccess === 1) {
            this.gtwSnackbarService.openSnackBar('Selected cache evicted', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Evict selected failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.evictCacheLoading = false;
          this.gtwSnackbarService.openSnackBar('Evict selected failed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  refreshCacheObj(objectId: number): void {
    this.cacheControlService.refreshCachedObject(this.commonApiUrl, objectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Refresh operation completed successfully.', 'gtw-snackbar--green');
            this.loadCacheControlList();
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Refresh failed.', 'gtw-snackbar--red');
          }
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Refresh failed.', 'gtw-snackbar--red');
        },
      });
  }

  downloadCacheObj(objectId: number, redisKey: string): void {
    this.cacheControlService.downloadCachedObject(this.commonApiUrl, objectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const blob = new Blob([resp.body], { type: 'text/plain' });
          const filename = (resp.headers?.get('filename') || redisKey || 'cache-object') + '.txt';
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
          this.gtwSnackbarService.openSnackBar('Download started.', 'gtw-snackbar--green');
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Download failed.', 'gtw-snackbar--red');
        },
      });
  }

  removeCacheObj(objectId: number): void {
    this.cacheControlService.removeCachedObject(this.commonApiUrl, objectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Remove operation completed successfully.', 'gtw-snackbar--green');
            this.loadCacheControlList();
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Remove failed.', 'gtw-snackbar--red');
          }
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Remove failed.', 'gtw-snackbar--red');
        },
      });
  }

  onToggleChange(row: CacheObjRow, event: any): void {
    const switchVal = event?.checkboxState === true;
    row.switchVal = switchVal;
    this.cacheSwitch(row, switchVal);
    this.cdr.detectChanges();
  }

  cacheSwitch(row: CacheObjRow, switchVal: boolean): void {
    const useCache: 'Y' | 'N' = switchVal ? 'Y' : 'N';
    this.cacheControlService.enableDisableCaching(this.commonApiUrl, row.object_id, useCache)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Enable/disable operation completed successfully.', 'gtw-snackbar--green');
          } else {
            // 200 + callSuccess=0: surface the API message and roll back the
            // optimistic toggle so the UI matches the server state.
            this.revertCacheToggle(row, resp?.errorMessage || 'Enable/disable failed.');
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          // HTTP error code (e.g. no access to client resources): surface the
          // API message and roll back the optimistic toggle.
          this.revertCacheToggle(row, this.extractApiErrorMessage(err, 'Enable/disable failed.'));
          this.cdr.detectChanges();
        },
      });
  }

  private revertCacheToggle(row: CacheObjRow, message: string): void {
    row.switchVal = !row.switchVal;
    this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--red');
  }

  /**
   * Extracts a human-readable message from an HttpErrorResponse: a raw string
   * body, a `{ errorMessage | error | message }` object, or the error itself.
   */
  private extractApiErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const fromBody = body.errorMessage || body.error || body.message;
      if (fromBody) {
        return fromBody;
      }
    }
    return err?.errorMessage || err?.message || fallback;
  }

  // ────────────────── JSON Diff modal ──────────────────

  showDiffModal(name: string, filter: string): void {
    this.showDiffDialog = true;
    this.showJsonLoader = true;
    this.diffRedisJsonView = '';
    this.diffDbJsonView = '';
    this.diffChangesView = '';
    this.cdr.detectChanges();

    const fromCache$ = this.cacheControlService.obtainBOFromCache(this.commonApiUrl, name, filter, 'Y');
    const fromDb$ = this.cacheControlService.obtainBOFromCache(this.commonApiUrl, name, filter, 'N');

    forkJoin([fromCache$, fromDb$])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([cacheResp, dbResp]: any[]) => {
          this.showJsonLoader = false;

          this.diffRedisCount = Number(cacheResp?.cachedData?.[0]?.CachedObjectSize || 0);
          this.diffDbCount = Number(dbResp?.cachedData?.[0]?.CachedObjectSize || 0);

          this.diffRedisJsonView = this.objToJsonHtml(cacheResp);
          this.diffDbJsonView = this.objToJsonHtml(dbResp);

          const diff = this.diffOwnProperties(cacheResp, dbResp);
          this.diffChangesView = this.diffToJsonHtml(diff);
          this.cdr.detectChanges();
        },
        error: () => {
          this.showJsonLoader = false;
          this.gtwSnackbarService.openSnackBar('Unable to load JSON diff data.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  closeDiffModal(): void {
    this.showDiffDialog = false;
    this.showJsonLoader = false;
    this.cdr.detectChanges();
  }

  // ────────────────── Tab 2: System Cache ──────────────────

  getAllCacheData(cache: string): void {
    this.selectedCache = cache;
    this.keyData = this.cacheDropdownObj?.[cache] || [];
    this.showSelectMsg = true;
    this.showJSON = false;
    this.taskModel = null;
    this.cdr.detectChanges();
  }

  getFilteredKeys(keys: string[]): string[] {
    if (!keys || !this.searchText) return keys || [];
    const s = this.searchText.toLowerCase();
    return keys.filter(k => k.toLowerCase().includes(s));
  }

  getKeyJsonByName(cache: string, keyName: string): void {
    this.taskModel = keyName || null;
    if (!cache || !this.taskModel) return;
    this.showJsonLoader = true;
    this.showJSON = false;
    this.showSelectMsg = false;
    this.cdr.detectChanges();
    this.cacheControlService.getCacheData(this.commonApiUrl, cache, this.taskModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.cacheKeyJsonView = JSON.stringify(resp?.jsonObject || resp || {}, null, 2);
          this.showJSON = true;
          this.showJsonLoader = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.showJsonLoader = false;
          this.gtwSnackbarService.openSnackBar('Error loading key data.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ────────────────── Tab 3: Index Deletion ──────────────────

  performDeletion(): void {
    this.deletionLoader = true;
    this.cdr.detectChanges();
    this.cacheControlService.deleteIndexes(this.baseUrl, this.selectedType || 'false')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.deletionLoader = false;
          if (String(resp?.callSuccess) === '1' || resp?.callSuccess === 1) {
            this.gtwSnackbarService.openSnackBar('Indexes deleted successfully.', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Index deletion failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.deletionLoader = false;
          this.gtwSnackbarService.openSnackBar('Index deletion failed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ────────────────── Common ──────────────────

  refresh(): void {
    this.loadCacheControlList();
    this.loadCacheKeys();
  }

  setOutPut(value: string): void { this.cacheForm.proc_params_out = value || ''; }
  setInPut(value: string): void { this.cacheForm.proc_params_in = value || ''; }
  setProc(value: string): void { this.cacheForm.proc_call = value || ''; }
  setFilters(value: string): void { this.cacheForm.filter_params = value || ''; }

  // ────────────────── JSON helpers (replaces angular-object-diff) ──────────────────

  private escHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** Equivalent of ObjectDiff.objToJsonView – formatted JSON as HTML */
  private objToJsonHtml(obj: any): string {
    try {
      const json = JSON.stringify(obj ?? {}, null, 2);
      return this.escHtml(json);
    } catch {
      return '{}';
    }
  }

  /**
   * Equivalent of ObjectDiff.diffOwnProperties.
   * Returns a diff descriptor for each property: { type, value?, oldValue?, children? }
   */
  private diffOwnProperties(left: any, right: any): any {
    if (left === right) return { type: 'unchanged', value: left };

    const leftIsObj = left !== null && typeof left === 'object' && !Array.isArray(left);
    const rightIsObj = right !== null && typeof right === 'object' && !Array.isArray(right);

    if (leftIsObj && rightIsObj) {
      const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
      const children: Record<string, any> = {};
      let hasChanges = false;
      keys.forEach(k => {
        const inLeft = left.hasOwnProperty(k);
        const inRight = right.hasOwnProperty(k);
        if (inLeft && !inRight) {
          children[k] = { type: 'removed', value: left[k] };
          hasChanges = true;
        } else if (!inLeft && inRight) {
          children[k] = { type: 'added', value: right[k] };
          hasChanges = true;
        } else {
          const childDiff = this.diffOwnProperties(left[k], right[k]);
          children[k] = childDiff;
          if (childDiff.type !== 'unchanged') hasChanges = true;
        }
      });
      return { type: hasChanges ? 'changed' : 'unchanged', objType: 'object', children };
    }

    const leftIsArr = Array.isArray(left);
    const rightIsArr = Array.isArray(right);

    if (leftIsArr && rightIsArr) {
      const maxLen = Math.max(left.length, right.length);
      const items: any[] = [];
      let hasChanges = false;
      for (let i = 0; i < maxLen; i++) {
        if (i >= left.length) {
          items.push({ type: 'added', value: right[i] });
          hasChanges = true;
        } else if (i >= right.length) {
          items.push({ type: 'removed', value: left[i] });
          hasChanges = true;
        } else {
          const childDiff = this.diffOwnProperties(left[i], right[i]);
          items.push(childDiff);
          if (childDiff.type !== 'unchanged') hasChanges = true;
        }
      }
      return { type: hasChanges ? 'changed' : 'unchanged', objType: 'array', children: items };
    }

    if (left === undefined) return { type: 'added', value: right };
    if (right === undefined) return { type: 'removed', value: left };

    return { type: 'changed', oldValue: left, newValue: right };
  }

  /** Equivalent of ObjectDiff.toJsonView – render diff as colored HTML */
  private diffToJsonHtml(diff: any): string {
    const lines: string[] = [];
    this.renderDiffNode(diff, lines, 0);
    return lines.join('\n');
  }

  private renderDiffNode(node: any, lines: string[], indent: number): void {
    const pad = '  '.repeat(indent);

    if (!node || typeof node !== 'object' || !node.type) {
      lines.push(`${pad}${this.escHtml(JSON.stringify(node))}`);
      return;
    }

    if (node.type === 'unchanged') {
      if (node.children && node.objType === 'object') {
        this.renderObjChildren(node.children, lines, indent);
      } else if (node.children && node.objType === 'array') {
        this.renderArrChildren(node.children, lines, indent);
      } else {
        lines.push(`${pad}${this.escHtml(JSON.stringify(node.value))}`);
      }
      return;
    }

    if (node.type === 'added') {
      const val = JSON.stringify(node.value, null, 2);
      val.split('\n').forEach(l =>
        lines.push(`<span class="diff-added">${pad}${this.escHtml(l)}</span>`));
      return;
    }

    if (node.type === 'removed') {
      const val = JSON.stringify(node.value, null, 2);
      val.split('\n').forEach(l =>
        lines.push(`<span class="diff-removed">${pad}${this.escHtml(l)}</span>`));
      return;
    }

    if (node.type === 'changed') {
      if (node.children && node.objType === 'object') {
        this.renderObjChildren(node.children, lines, indent);
      } else if (node.children && node.objType === 'array') {
        this.renderArrChildren(node.children, lines, indent);
      } else {
        const oldVal = JSON.stringify(node.oldValue, null, 2);
        const newVal = JSON.stringify(node.newValue, null, 2);
        oldVal.split('\n').forEach(l =>
          lines.push(`<span class="diff-removed">${pad}${this.escHtml(l)}</span>`));
        newVal.split('\n').forEach(l =>
          lines.push(`<span class="diff-added">${pad}${this.escHtml(l)}</span>`));
      }
    }
  }

  private renderObjChildren(children: Record<string, any>, lines: string[], indent: number): void {
    const pad = '  '.repeat(indent);
    lines.push(`${pad}{`);
    const keys = Object.keys(children);
    keys.forEach((key, i) => {
      const child = children[key];
      const comma = i < keys.length - 1 ? ',' : '';
      const keyStr = `${this.escHtml(JSON.stringify(key))}: `;
      const innerPad = '  '.repeat(indent + 1);

      if (child.type === 'unchanged' && !child.children) {
        lines.push(`${innerPad}${keyStr}${this.escHtml(JSON.stringify(child.value))}${comma}`);
      } else if (child.type === 'added') {
        const val = JSON.stringify(child.value);
        lines.push(`<span class="diff-added">${innerPad}${keyStr}${this.escHtml(val)}${comma}</span>`);
      } else if (child.type === 'removed') {
        const val = JSON.stringify(child.value);
        lines.push(`<span class="diff-removed">${innerPad}${keyStr}${this.escHtml(val)}${comma}</span>`);
      } else if (child.type === 'changed' && !child.children) {
        lines.push(`<span class="diff-removed">${innerPad}${keyStr}${this.escHtml(JSON.stringify(child.oldValue))}${comma}</span>`);
        lines.push(`<span class="diff-added">${innerPad}${keyStr}${this.escHtml(JSON.stringify(child.newValue))}${comma}</span>`);
      } else if (child.children) {
        lines.push(`${innerPad}${keyStr}`);
        this.renderDiffNode(child, lines, indent + 1);
        const lastIdx = lines.length - 1;
        lines[lastIdx] = lines[lastIdx] + comma;
      }
    });
    lines.push(`${pad}}`);
  }

  private renderArrChildren(children: any[], lines: string[], indent: number): void {
    const pad = '  '.repeat(indent);
    lines.push(`${pad}[`);
    children.forEach((child, i) => {
      const comma = i < children.length - 1 ? ',' : '';
      const innerPad = '  '.repeat(indent + 1);

      if (child.type === 'unchanged' && !child.children) {
        lines.push(`${innerPad}${this.escHtml(JSON.stringify(child.value))}${comma}`);
      } else if (child.type === 'added') {
        const val = JSON.stringify(child.value);
        lines.push(`<span class="diff-added">${innerPad}${this.escHtml(val)}${comma}</span>`);
      } else if (child.type === 'removed') {
        const val = JSON.stringify(child.value);
        lines.push(`<span class="diff-removed">${innerPad}${this.escHtml(val)}${comma}</span>`);
      } else if (child.type === 'changed' && !child.children) {
        lines.push(`<span class="diff-removed">${innerPad}${this.escHtml(JSON.stringify(child.oldValue))}${comma}</span>`);
        lines.push(`<span class="diff-added">${innerPad}${this.escHtml(JSON.stringify(child.newValue))}${comma}</span>`);
      } else if (child.children) {
        this.renderDiffNode(child, lines, indent + 1);
        const lastIdx = lines.length - 1;
        lines[lastIdx] = lines[lastIdx] + comma;
      }
    });
    lines.push(`${pad}]`);
  }
}
